import React, { useState, useRef } from 'react';
import { SimulationTemplate, SimulationStep, ResultBlock, GraphContainer, ResultMessage } from '../SimulationTemplate';
import { Text } from '../../../components/ui/typography/Text';
import { AccentButton } from '../../../components/ui/buttons/AccentButton';
import { OutlineButton } from '../../../components/ui/buttons/OutlineButton';
import { Brain, Microscope, BookOpen, Star, X, Maximize2, BarChart3 } from 'lucide-react';
import beachImg from '../../../assets/rosy-retrospection/beach.png';
import marketImg from '../../../assets/rosy-retrospection/market.png';
import rainyDayImg from '../../../assets/rosy-retrospection/rainy_day.png';
import { DistractionStage } from './DistractionStage';
import { Portal } from '../../../components/ui/Portal';

// Add this helper function to wrap text
const wrapText = (text: string, maxLength = 25) => {
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
  const effectiveMaxLength = isMobile ? 12 : maxLength;  // Even shorter length on mobile
  
  if (text.length <= effectiveMaxLength) return [text];
  
  const words = text.split(' ');
  const lines: string[] = [];
  let currentLine = words[0];

  for (let i = 1; i < words.length; i++) {
    const word = words[i];
    if ((currentLine + ' ' + word).length <= effectiveMaxLength) {
      currentLine += ' ' + word;
    } else {
      lines.push(currentLine);
      currentLine = word;
    }
  }
  lines.push(currentLine);
  return lines;
};

const scenes = [
  {
    description: "Day 1: Arrival at the beach resort",
    image: beachImg,
    alt: "Tropical beach resort with palm trees, lounge chairs, umbrellas, and a pool by the ocean on a sunny day.",
    scenario: "You've just arrived at a beautiful beach resort. The weather is perfect, and you're excited to start your vacation."
  },
  {
    description: "Day 2: Exploring local markets",
    image: marketImg,
    alt: "Colorful outdoor market with fresh fruits and vegetables under vibrant umbrellas in a lively street scene.",
    scenario: "You're exploring vibrant local markets, trying new foods and experiencing the local culture."
  },
  {
    description: "Day 3: Rainy day at the resort",
    image: rainyDayImg,
    alt: "Cozy resort lounge with armchairs and tables, looking out through large windows at palm trees and rain.",
    scenario: "It's raining today, so you're spending time indoors at the resort's lounge area, reading and relaxing."
  }
];

interface PhotoCredit {
  photographer: string;
  photographerUrl: string;
  photoUrl: string;
  altText: string;
}

const photoCredits: Record<string, PhotoCredit> = {
  "A busy café with people chatting": {
    photographer: "Rod Long",
    photographerUrl: "https://unsplash.com/@rodlong",
    photoUrl: "https://unsplash.com/photos/group-of-people-eating-on-restaurant-I79Pgmhmy5M",
    altText: "Interior of a busy café with people sitting at tables, warm lighting from hanging lamps, and a menu board visible in the background"
  },
  "A park on a cloudy day": {
    photographer: "Krišjānis Kazaks",
    photographerUrl: "https://unsplash.com/@kazaks",
    photoUrl: "https://unsplash.com/photos/a-yellow-object-in-a-grassy-area-Zj32O7LN_b8",
    altText: "A serene park landscape under overcast skies with trees and grass creating a calm atmosphere"
  },
  "A quiet library study area": {
    photographer: "Claudio Schwarz",
    photographerUrl: "https://unsplash.com/@purzlbaum",
    photoUrl: "https://unsplash.com/photos/man-sitting-on-chair-inside-library-yUSLlcOkJnk",
    altText: "A peaceful library study space with wooden tables, bookshelves, and natural lighting through large windows"
  }
};

// Add RatingButtons component at the top level
interface RatingButtonsProps {
  onRate: (rating: number) => void;
  selectedRating: number | null;
  stageType: 'viewing' | 'memory' | 'prediction';
  sceneIndex: number;
}

const RatingButtons: React.FC<RatingButtonsProps> = ({ 
  onRate, 
  selectedRating, 
  stageType,
  sceneIndex
}) => (
  <div className="space-y-4">
    <Text className="text-center text-gray-700">
      {stageType === 'prediction' 
        ? "How enjoyable do you think this vacation will be overall?"
        : "How enjoyable is this experience right now?"}
    </Text>

    <div className="flex flex-wrap justify-center gap-2">
      {[1, 2, 3, 4, 5, 6, 7].map((rating) => (
        <OutlineButton
          key={`${stageType}-scene-${sceneIndex}-rating-${rating}`}
          onClick={() => onRate(rating)}
          className="w-12 h-12 flex items-center justify-center text-lg"
          active={selectedRating === rating}
        >
          {rating}
        </OutlineButton>
      ))}
    </div>

    <Text className="text-center text-sm text-gray-500">
      1 = Not at all enjoyable, 7 = Very enjoyable
    </Text>
  </div>
);

const TemplatedRosyRetrospectionSimulation: React.FC = () => {
  // ========== STATE MANAGEMENT ==========
  const [started, setStarted] = useState(false);
  const [stage, setStage] = useState<'prediction' | 'viewing' | 'distraction' | 'memory' | 'overall-memory' | 'results'>('prediction');
  const [predictionRating, setPredictionRating] = useState<number | null>(null);
  const [initialRatings, setInitialRatings] = useState<number[]>([]);
  const [memoryRatings, setMemoryRatings] = useState<number[]>([]);
  const [overallMemoryRating, setOverallMemoryRating] = useState<number | null>(null);
  const [currentSceneIndex, setCurrentSceneIndex] = useState(0);
  const [imagesPreloaded, setImagesPreloaded] = useState(false);
  const [viewingRatings, setViewingRatings] = useState<Record<number, number>>({});
  const [memorySceneRatings, setMemorySceneRatings] = useState<Record<number, number>>({});
  const [lightboxImage, setLightboxImage] = useState<string | null>(null);
  const [showResults, setShowResults] = useState(false);
  
  // Reference for scrolling
  const simulationRef = useRef<HTMLDivElement>(null);

  // ========== CHART DIMENSIONS ==========
  // First, update the base dimensions and padding
  const baseWidth = 700;
  const baseHeight = 400;
  const basePadding = { 
    top: 40, 
    right: 20,
    bottom: 60, 
    left: 180  // Default for desktop
  };

  const [dimensions, setDimensions] = React.useState({
    width: baseWidth,
    height: baseHeight,
    margin: basePadding
  });

  // Update the updateDimensions function
  React.useEffect(() => {
    const updateDimensions = () => {
      const isMobile = window.innerWidth < 768;
      if (isMobile) {
        setDimensions({
          width: Math.min(window.innerWidth - 32, baseWidth),
          height: baseHeight,
          margin: {
            ...basePadding,
            left: 120, // Reduced left margin for mobile
            bottom: 80
          }
        });
      } else {
        setDimensions({
          width: baseWidth,
          height: baseHeight,
          margin: basePadding
        });
      }
    };

    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    return () => window.removeEventListener('resize', updateDimensions);
  }, []);

  // Preload images
  React.useEffect(() => {
    const preloadImages = async () => {
      const imagePromises = scenes.map(scene => {
        return new Promise((resolve, reject) => {
          const img = new Image();
          img.src = scene.image;
          img.onload = resolve;
          img.onerror = reject;
        });
      });

      try {
        await Promise.all(imagePromises);
        setImagesPreloaded(true);
      } catch (error) {
        console.error('Error preloading images:', error);
        // Still set as preloaded to not block the simulation
        setImagesPreloaded(true);
      }
    };

    preloadImages();
  }, []);

  // ========== EVENT HANDLERS ==========
  const handlePredictionRating = (rating: number) => {
    setPredictionRating(rating);
    setStage('viewing');
  };

  const handleInitialRating = (rating: number) => {
    setViewingRatings(prev => ({
      ...prev,
      [currentSceneIndex]: rating
    }));
    const newRatings = [...initialRatings, rating];
    if (newRatings.length === scenes.length) {
      setStage('distraction');
    } else {
      setCurrentSceneIndex(prev => prev + 1);
    }
    setInitialRatings(newRatings);
  };

  const handleMemoryRating = (rating: number) => {
    setMemorySceneRatings(prev => ({
      ...prev,
      [currentSceneIndex]: rating
    }));
    const newRatings = [...memoryRatings, rating];
    if (newRatings.length === scenes.length) {
      setStage('overall-memory');
      setMemoryRatings(newRatings);
    } else {
      setCurrentSceneIndex(prev => prev + 1);
      setMemoryRatings(newRatings);
    }
  };

  const handleOverallMemoryRating = (rating: number) => {
    setOverallMemoryRating(rating);
    setStage('results');
    setShowResults(true);
  };

  const startMemoryPhase = () => {
    setStage('memory');
    setCurrentSceneIndex(0);
    setMemorySceneRatings({});
  };

  // Reset the simulation
  const handleReset = () => {
    // Reset all states
    setStarted(false);
    setStage('prediction');
    setPredictionRating(null);
    setInitialRatings([]);
    setMemoryRatings([]);
    setOverallMemoryRating(null);
    setCurrentSceneIndex(0);
    setViewingRatings({});
    setMemorySceneRatings({});
    setShowResults(false);
    
    // Scroll back to top of simulation
    simulationRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const openLightbox = (image: string) => {
    setLightboxImage(image);
    // Prevent body scroll when lightbox is open
    document.body.style.overflow = 'hidden';
  };

  const closeLightbox = () => {
    setLightboxImage(null);
    // Restore body scroll when lightbox is closed
    document.body.style.overflow = 'unset';
  };

  const renderLightbox = () => {
    if (!lightboxImage) return null;

    return (
      <Portal>
        <div 
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
          onClick={closeLightbox}
        >
          <button
            onClick={closeLightbox}
            className="absolute top-4 right-4 text-white p-2 rounded-full bg-black/50 hover:bg-black/70 transition-colors"
            aria-label="Close lightbox"
          >
            <X className="w-6 h-6" />
          </button>
          <img
            src={lightboxImage}
            alt="Enlarged view"
            className="max-w-full max-h-[90vh] object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      </Portal>
    );
  };

  const renderPredictionStage = () => (
    <div className="space-y-6">
      <Text className="text-center text-lg font-medium">
        Before we begin, please predict how enjoyable you think this 3-day vacation will be overall.
      </Text>
      <RatingButtons
        onRate={handlePredictionRating}
        selectedRating={predictionRating}
        stageType="prediction"
        sceneIndex={0}
      />
    </div>
  );

  const renderScene = (scene: typeof scenes[0], onRate: (rating: number) => void) => (
    <div className="space-y-6">
      <Text className="text-center text-lg font-medium">
        {scene.description}
      </Text>
      <Text className="text-center text-gray-700">
        {scene.scenario}
      </Text>
      <div className="relative">
        <img
          src={scene.image}
          alt={scene.alt}
          className="w-full h-72 object-contain rounded-lg cursor-pointer"
          onClick={() => openLightbox(scene.image)}
        />
        <button
          onClick={() => openLightbox(scene.image)}
          className="absolute top-2 right-2 p-2 bg-white/80 rounded-full hover:bg-white transition-colors"
        >
          <Maximize2 className="w-4 h-4" />
        </button>
      </div>
      <RatingButtons
        onRate={onRate}
        selectedRating={stage === 'viewing' ? viewingRatings[currentSceneIndex] : memorySceneRatings[currentSceneIndex]}
        stageType={stage === 'viewing' ? 'viewing' : 'memory'}
        sceneIndex={currentSceneIndex}
      />
    </div>
  );

  // ========== CHART CALCULATIONS ==========
  const chartWidth = dimensions.width - dimensions.margin.left - dimensions.margin.right;
  const chartHeight = dimensions.height - dimensions.margin.top - dimensions.margin.bottom;
  const rowHeight = chartHeight / (scenes.length + 1); // Add 1 for the overall assessment row

  // Scale function adjusted for responsive width
  const scaleX = (value: number) => {
    return ((value - 1) / 6) * chartWidth;
  };

  // ========== SIMULATION CONTENT ==========
  const simulationContent = (
    <div ref={simulationRef} className="space-y-8">
      {/* PREDICTION STAGE */}
      {stage === 'prediction' && (
        <SimulationStep 
          title="Predict Your Experience"
          isActive={true}
        >
          {renderPredictionStage()}
        </SimulationStep>
      )}

      {/* VIEWING STAGE */}
      {stage === 'viewing' && (
        <SimulationStep 
          title="Experiencing Your Vacation"
          isActive={true}
        >
          {renderScene(scenes[currentSceneIndex], handleInitialRating)}
        </SimulationStep>
      )}

      {/* DISTRACTION STAGE */}
      {stage === 'distraction' && (
        <SimulationStep 
          title="Quick Math Challenge" 
          isActive={true}
        >
          <DistractionStage onComplete={startMemoryPhase} />
        </SimulationStep>
      )}

      {/* MEMORY STAGE */}
      {stage === 'memory' && (
        <SimulationStep 
          title="Rate from Memory" 
          isActive={true}
        >
          <div className="space-y-4">
            <Text className="text-gray-700">
              Now, thinking back to the scenes you saw earlier, how pleasant do you remember them being?
            </Text>
            <Text className="text-gray-700 font-medium text-center">
              {scenes[currentSceneIndex].description}
            </Text>
            <div key={`memory-scene-${currentSceneIndex}`}>
              <RatingButtons
                onRate={handleMemoryRating}
                selectedRating={memorySceneRatings[currentSceneIndex]}
                stageType="memory"
                sceneIndex={currentSceneIndex}
              />
            </div>
          </div>
        </SimulationStep>
      )}

      {/* OVERALL MEMORY STAGE */}
      {stage === 'overall-memory' && (
        <SimulationStep 
          title="Overall Impression" 
          isActive={true}
        >
          <div className="space-y-6">
            <Text className="text-center text-lg font-medium">
              Finally, thinking back to the entire vacation experience, how enjoyable was it overall?
            </Text>
            <RatingButtons
              onRate={handleOverallMemoryRating}
              selectedRating={overallMemoryRating}
              stageType="memory"
              sceneIndex={-1}
            />
          </div>
        </SimulationStep>
      )}

      {/* RESULTS SECTION */}
      {showResults && (
        <div className="space-y-6">
          {/* Understanding the Results Block */}
          <ResultBlock
            title="Understanding the Results"
            icon={Brain}
            variant="highlight"
          >
            <div className="space-y-8">
              {/* Step 1: User Results */}
              <div className="space-y-5">
                <Text className="text-gray-800 font-medium text-lg">
                  Your Results
                </Text>
                
                <ResultMessage 
                  isCorrect={Math.abs(memoryRatings.reduce((sum, rating) => sum + rating, 0) / memoryRatings.length - 
                  initialRatings.reduce((sum, rating) => sum + rating, 0) / initialRatings.length) <= 0.5}
                  message={
                    Math.abs(memoryRatings.reduce((sum, rating) => sum + rating, 0) / memoryRatings.length - 
                    initialRatings.reduce((sum, rating) => sum + rating, 0) / initialRatings.length) <= 0.5
                      ? "You remembered the scenes very similarly to how you initially rated them. This suggests you resisted the typical 'rosy retrospection' effect, where memories tend to become more positive over time."
                      : memoryRatings.reduce((sum, rating) => sum + rating, 0) / memoryRatings.length >
                        initialRatings.reduce((sum, rating) => sum + rating, 0) / initialRatings.length
                          ? "You remembered the scenes as more pleasant than you initially rated them. This demonstrates rosy retrospection - our tendency to view past experiences more favorably than when we first experienced them."
                          : "Interestingly, you remembered the scenes as less pleasant than your initial ratings. While most people tend to remember experiences more positively (rosy retrospection), individual results can vary."
                  }
                />
                
                {/* Using GraphContainer for user result visualization */}
                <GraphContainer>
                  <div className="w-full overflow-x-auto">
                    <div style={{ minWidth: Math.min(700, window.innerWidth - 32), maxWidth: '100%' }} className="mx-auto">
                      <svg 
                        width={dimensions.width}
                        height={dimensions.height}
                        className="mx-auto block"
                      >
                        <g transform={`translate(${dimensions.margin.left}, ${dimensions.margin.top})`}>
                          {/* X axis with ticks */}
                          <line x1={0} y1={chartHeight} x2={chartWidth} y2={chartHeight} stroke="#888" />
                          {[1, 2, 3, 4, 5, 6, 7].map(tick => (
                            <g key={`tick-${tick}`}>
                              <line 
                                x1={scaleX(tick)} 
                                y1={chartHeight} 
                                x2={scaleX(tick)} 
                                y2={chartHeight + 5} 
                                stroke="#888" 
                              />
                              <text 
                                x={scaleX(tick)} 
                                y={chartHeight + 20} 
                                textAnchor="middle" 
                                fontSize={12} 
                                fill="#666"
                              >
                                {tick}
                              </text>
                            </g>
                          ))}

                          {/* Y axis line */}
                          <line x1={0} y1={0} x2={0} y2={chartHeight} stroke="#888" />

                          {/* Grid lines */}
                          {scenes.map((_, i) => {
                            const y = i * rowHeight + rowHeight / 2;
                            return (
                              <line 
                                key={`grid-${i}`}
                                x1={0} 
                                y1={y} 
                                x2={chartWidth} 
                                y2={y} 
                                stroke="#eee" 
                                strokeDasharray="4,4"
                              />
                            );
                          })}

                          {/* Dumbbell chart elements */}
                          {[
                            // Add overall assessment as the first row
                            ...(predictionRating !== null && overallMemoryRating !== null ? [{
                              description: "Overall Vacation",
                              initialRating: predictionRating,
                              memoryRating: overallMemoryRating
                            }] : []),
                            // Add all scene ratings
                            ...scenes.map((scene, i) => ({
                              description: scene.description,
                              initialRating: initialRatings[i],
                              memoryRating: memoryRatings[i]
                            }))
                          ].map((item, i) => {
                            const y = i * rowHeight + rowHeight / 2;
                            const lines = wrapText(item.description);
                            const scoresMatch = item.initialRating === item.memoryRating;
                            
                            return (
                              <g key={`row-${i}`}>
                                {/* Scene description */}
                                {lines.map((line, lineIndex) => (
                                  <text 
                                    key={lineIndex}
                                    x={-12}
                                    y={y + (lineIndex - (lines.length - 1) / 2) * (window.innerWidth < 768 ? 14 : 18)}
                                    textAnchor="end" 
                                    fontSize={window.innerWidth < 768 ? 11 : 13}
                                    fill="#333"
                                  >
                                    {line}
                                  </text>
                                ))}

                                {/* Connecting line - only show if scores are different */}
                                {!scoresMatch && (
                                  <line 
                                    x1={scaleX(item.initialRating)} 
                                    y1={y} 
                                    x2={scaleX(item.memoryRating)} 
                                    y2={y} 
                                    stroke="#aaa" 
                                    strokeWidth={2} 
                                  />
                                )}

                                {scoresMatch ? (
                                  // When scores match, show dots side by side
                                  <g>
                                    {/* Initial rating point */}
                                    <circle 
                                      cx={scaleX(item.initialRating) - 4} 
                                      cy={y} 
                                      r={6} 
                                      fill="#3b82f6" 
                                    />
                                    {/* Memory rating point */}
                                    <circle 
                                      cx={scaleX(item.memoryRating) + 4} 
                                      cy={y} 
                                      r={6} 
                                      fill="#f97316" 
                                    />
                                    {/* Single score label */}
                                    <text 
                                      x={scaleX(item.initialRating)} 
                                      y={y - 12} 
                                      textAnchor="middle" 
                                      fontSize={11} 
                                      fill="#666"
                                    >
                                      {item.initialRating}
                                    </text>
                                  </g>
                                ) : (
                                  // When scores are different, show normal dumbbell
                                  <g>
                                    {/* Initial rating point */}
                                    <circle 
                                      cx={scaleX(item.initialRating)} 
                                      cy={y} 
                                      r={6} 
                                      fill="#3b82f6" 
                                    />
                                    {/* Memory rating point */}
                                    <circle 
                                      cx={scaleX(item.memoryRating)} 
                                      cy={y} 
                                      r={6} 
                                      fill="#f97316" 
                                    />
                                    {/* Both score labels */}
                                    <text 
                                      x={scaleX(item.initialRating)} 
                                      y={y - 10} 
                                      textAnchor="middle" 
                                      fontSize={11} 
                                      fill="#666"
                                    >
                                      {item.initialRating}
                                    </text>
                                    <text 
                                      x={scaleX(item.memoryRating)} 
                                      y={y - 10} 
                                      textAnchor="middle" 
                                      fontSize={11} 
                                      fill="#666"
                                    >
                                      {item.memoryRating}
                                    </text>
                                  </g>
                                )}
                              </g>
                            );
                          })}

                          {/* Legend */}
                          <g transform={`translate(${chartWidth/2 - 120}, ${chartHeight + (window.innerWidth < 768 ? 60 : 40)})`}>
                            <circle cx={10} cy={10} r={6} fill="#3b82f6" />
                            <text x={25} y={14} fontSize={12} fill="#333">Initial Rating</text>
                            <circle cx={140} cy={10} r={6} fill="#f97316" />
                            <text x={155} y={14} fontSize={12} fill="#333">Memory Rating</text>
                          </g>
                        </g>
                      </svg>
                    </div>
                  </div>
                </GraphContainer>
              </div>
              
              {/* Step 2: Connection to the Bias */}
              <div className="space-y-3">
                <Text className="text-gray-800 font-medium text-lg">
                  How This Relates to Rosy Retrospection
                </Text>
                <Text className="text-gray-700">
                  In this simulation, the comparison between your real-time ratings and memory ratings demonstrates rosy retrospection in action. This cognitive bias influences how we make decisions about future experiences based on potentially distorted memories. When we remember vacations, social events, or projects as more enjoyable than they actually were, we're more likely to repeat similar experiences, even if they weren't optimal at the time. This explains why people often return to the same vacation spots despite previously experiencing weather issues, crowds, or other inconveniences they later minimize in memory.
                </Text>
              </div>
              
              {/* Step 3: Study Findings */}
              <div className="space-y-3">
                <Text className="text-gray-800 font-medium text-lg">
                  What the Original Study Found
                </Text>
                <Text className="text-gray-700">
                  In Mitchell, Thompson, Peterson, and Cronk's research:
                </Text>
                <ul className="list-disc list-inside space-y-2 text-gray-700">
                  <li>Researchers examined people's experiences during three distinct events: a vacation, a trip to an amusement park, and a 3-week bicycle tour across California</li>
                  <li>Participants were asked to rate their experiences in three phases: before (anticipation), during (experience), and after (remembering) each event</li>
                  <li>The study found that people consistently rated both their anticipated and remembered enjoyment higher than their actual experiences</li>
                </ul>
                <Text className="text-gray-700">
                  They called this phenomenon "rosy retrospection" - the tendency to remember past events more favorably than they were experienced in the moment. This research demonstrated how our memories selectively filter experiences, often enhancing positive aspects while minimizing negative ones.
                </Text>
              </div>
            </div>
          </ResultBlock>

          {/* About the Study Block */}
          <ResultBlock
            title="About the Study"
            icon={BookOpen}
            variant="muted"
          >
            <div className="space-y-4">
              <Text className="text-gray-700">
                Based on research by Mitchell, Thompson, Peterson, and Cronk (1997), "Temporal Adjustments in the Evaluation of Events: The 'Rosy View'," published in the Journal of Experimental Social Psychology. Their groundbreaking work identified how people tend to rate experiences more positively both before they occur (anticipation) and after they've happened (remembering), compared to during the actual experience.
              </Text>
              
              <Text className="text-sm text-gray-500 italic">
                This simulation is designed for educational purposes. Individual results may vary.
              </Text>
            </div>
          </ResultBlock>

          {/* Try Again Button */}
          <div className="flex justify-center">
            <AccentButton onClick={handleReset}>
              Try Again
            </AccentButton>
          </div>
        </div>
      )}
    </div>
  );

  // ========== MAIN COMPONENT RENDER ==========
  return (
    <SimulationTemplate
      title="Rosy Retrospection"
      description="Experience how our memories of past events can become more positive over time."
    >
      {!started ? (
        <div className="space-y-6">
          <Text>
            In this simulation, you'll experience how our memories of past events can become more positive over time, even if the actual experience wasn't as enjoyable as we remember it.
          </Text>
          <Text>
            You'll go through a simulated 3-day vacation, rating your experience in real-time and then recalling it later. This will help demonstrate the rosy retrospection effect.
          </Text>
          <div className="flex justify-center">
            <AccentButton onClick={() => {
              setStarted(true);
              setStage('prediction');
            }}>
              Start Simulation
            </AccentButton>
          </div>
        </div>
      ) : (
        simulationContent
      )}
      {lightboxImage && renderLightbox()}
    </SimulationTemplate>
  );
};

export default TemplatedRosyRetrospectionSimulation; 